import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';

/**
 * Jumio identity-verification badge shown next to a person's name.
 *
 * Rendered wherever a user is named — the assigned-driver panel, step-history
 * resource cards and the resource assignment dropdowns — so a dispatcher can
 * tell at a glance whether the person on a step has actually verified their
 * licence.
 *
 * The badge is deliberately ABSENT (not "unverified") when the person's
 * resource type has no `license_verification` field: there is nothing for them
 * to verify, so an unverified badge would be a permanent false alarm. See
 * ResourceDirectoryUsecaseProvider.deriveVerificationState, which is the
 * server-side twin of deriveJumioVerification below and produces the same
 * states.
 */

export type JumioVerificationState =
  | 'verified'
  | 'unverified'
  | 'processing'
  | 'not_captured'
  /** The resource type has no licence field — nothing to verify, no badge. */
  | 'not_applicable'
  /** Local derivation couldn't tell (resource type not populated on this object).
   *  Also renders no badge, but useJumioVerifications resolves these over the API. */
  | 'unknown';

export interface JumioVerification {
  /** false when the resource type has no licence field — render nothing. */
  applicable: boolean;
  state: JumioVerificationState;
  verified?: boolean;
  fieldName?: string;
  confidence?: number | null;
  message?: string | null;
  documentType?: string | null;
  verifiedAt?: string | null;
  capturedAt?: string | null;
}

/** Map of ResourceData uid (and _id) → verification, as returned by the batch endpoint. */
export type JumioVerificationMap = Record<string, JumioVerification>;

const isLicenceFieldDef = (f: any) => f?.type === 'license_verification';

/**
 * Derive the verification state client-side from a resource object that already
 * carries both its populated resource type and its data — which is exactly what
 * `GET /resource/search` returns, so the dropdowns need no extra round-trip.
 *
 * Returns a non-applicable result when the shape is missing either half; the
 * caller then simply renders no badge.
 */
export function deriveJumioVerification(resource: any): JumioVerification {
  const unknown: JumioVerification = { applicable: false, state: 'unknown', verified: false };
  const notApplicable: JumioVerification = { applicable: false, state: 'not_applicable', verified: false };
  if (!resource || typeof resource !== 'object') return unknown;

  const resourceType = resource.resourceTypeId || resource.resourceType;
  const fieldDefs = Array.isArray(resourceType?.fields) ? resourceType.fields : null;
  // Without the type definition we cannot tell "no licence field configured"
  // apart from "licence not scanned yet", and guessing wrong would show a badge
  // that can never be cleared. Report `unknown` so the caller can resolve it
  // over the API instead; either way no badge is drawn until it's settled.
  if (!fieldDefs) return unknown;

  const licenceFields = fieldDefs.filter(isLicenceFieldDef);
  if (licenceFields.length === 0) return notApplicable;

  const data = resource.fields || resource.data || {};
  // Worst-first: a "Verified" badge must mean EVERY licence field is verified.
  const rank: Record<string, number> = { not_captured: 0, unverified: 1, processing: 2, verified: 3 };

  let worst: JumioVerification | null = null;
  for (const def of licenceFields) {
    const value = data?.[def.name];
    let entry: JumioVerification;
    if (!value || typeof value !== 'object') {
      entry = { applicable: true, state: 'not_captured', verified: false, fieldName: def.name };
    } else if (value.status === 'processing') {
      entry = {
        applicable: true,
        state: 'processing',
        verified: false,
        fieldName: def.name,
        capturedAt: value.capturedAt ?? null
      };
    } else {
      const ok = value.outcome === 'true';
      entry = {
        applicable: true,
        state: ok ? 'verified' : 'unverified',
        verified: ok,
        fieldName: def.name,
        confidence: value.confidence ?? null,
        message: value.message ?? null,
        documentType: value.documentType ?? null,
        verifiedAt: value.verifiedAt ?? null
      };
    }
    if (worst === null || rank[entry.state] < rank[worst.state]) worst = entry;
  }

  return worst as JumioVerification;
}

/**
 * Pull a resource's verification out of a batch-endpoint map, trying every id
 * the caller might be holding. Event snapshots carry `id` (the ResourceData
 * uid); other call sites have `_id` or `resourceDataId`.
 */
export function verificationFor(
  map: JumioVerificationMap | undefined | null,
  ...ids: Array<string | undefined | null>
): JumioVerification | undefined {
  if (!map) return undefined;
  for (const id of ids) {
    if (id && map[String(id)]) return map[String(id)];
  }
  return undefined;
}

type Tone = { color: string; bg: string; icon: JSX.Element; label: string; title: string };

// Two palettes: the light one for cards, the `onDark` one for the blue step
// headers and other dark surfaces, where the dark green/red read as near-black.
const PALETTE = {
  verified: { light: '#1b7f3b', dark: '#7ee2a8', bg: 'rgba(27,127,59,0.12)', bgDark: 'rgba(255,255,255,0.18)' },
  processing: { light: '#a15c00', dark: '#ffd28a', bg: 'rgba(245,166,35,0.16)', bgDark: 'rgba(255,255,255,0.18)' },
  unverified: { light: '#b3261e', dark: '#ff9d97', bg: 'rgba(179,38,30,0.10)', bgDark: 'rgba(255,255,255,0.18)' }
};

const toneFor = (v: JumioVerification, onDark: boolean): Tone | null => {
  const paint = (key: keyof typeof PALETTE) => ({
    color: onDark ? PALETTE[key].dark : PALETTE[key].light,
    bg: onDark ? PALETTE[key].bgDark : PALETTE[key].bg
  });

  switch (v.state) {
    case 'verified':
      return {
        ...paint('verified'),
        icon: <VerifiedIcon sx={{ fontSize: 'inherit' }} />,
        label: 'Verified',
        title:
          v.confidence != null
            ? `ID verified with Jumio (${v.confidence}% confidence)`
            : 'ID verified with Jumio'
      };
    case 'processing':
      return {
        ...paint('processing'),
        icon: <HourglassTopIcon sx={{ fontSize: 'inherit' }} />,
        label: 'Verifying',
        title: 'Licence scanned — waiting on the Jumio verdict'
      };
    case 'not_captured':
      return {
        ...paint('unverified'),
        icon: <GppMaybeIcon sx={{ fontSize: 'inherit' }} />,
        label: 'Unverified',
        title: 'No Jumio licence scan on file for this user'
      };
    case 'unverified':
      return {
        ...paint('unverified'),
        icon: <GppMaybeIcon sx={{ fontSize: 'inherit' }} />,
        label: 'Unverified',
        title: v.message || 'Jumio could not verify this user’s ID'
      };
    default:
      return null;
  }
};

interface Props {
  verification?: JumioVerification | null;
  /** `icon` is the inline form used beside a name; `chip` adds the text label. */
  variant?: 'icon' | 'chip';
  size?: number;
  /** Switch to the light-on-dark palette (blue step headers, coloured banners). */
  onDark?: boolean;
}

/**
 * Renders nothing when there is no verification to report (unknown resource, or
 * a resource type with no licence field) — an absent badge is not "unverified".
 */
const JumioVerificationBadge: React.FC<Props> = ({
  verification,
  variant = 'icon',
  size = 15,
  onDark = false
}) => {
  if (!verification || !verification.applicable) return null;
  const tone = toneFor(verification, onDark);
  if (!tone) return null;

  if (variant === 'icon') {
    return (
      <Tooltip title={tone.title}>
        <Box
          component="span"
          aria-label={tone.label}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            color: tone.color,
            fontSize: size,
            lineHeight: 1,
            cursor: 'default'
          }}
        >
          {tone.icon}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tone.title}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.4,
          flexShrink: 0,
          px: 0.6,
          py: 0.15,
          borderRadius: 1,
          bgcolor: tone.bg,
          color: tone.color,
          fontSize: size,
          lineHeight: 1.4,
          cursor: 'default'
        }}
      >
        {tone.icon}
        <Typography
          component="span"
          sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'inherit', textTransform: 'none' }}
        >
          {tone.label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default JumioVerificationBadge;
